import { useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ChevronLeft, ChevronRight, Landmark, BookOpen, Sun, Building2, Languages, Bus, UtensilsCrossed, Wallet, HeartPulse, Luggage, Plug, Home, MessageSquareHeart } from "lucide-react";
import Seo from "@/components/Seo";
import { FadeUp } from "@/components/Reveal";
import { CountryCard } from "@/components/CountryCard";
import { MomTakeSection } from "@/components/MomTake";
import { BudgetEstimator } from "@/components/BudgetEstimator";
import { TanzaniaSplit } from "@/components/TanzaniaSplit";
import { CommunityReviews } from "@/components/CommunityReviews";
import { TaxesTab } from "@/components/TaxesTab";
import { healthcareData, insuranceReqs } from "@/data/healthcare";
import { packingData } from "@/data/packing";
import { techData, simData } from "@/data/tech";
import { PlugIcon, parsePlugTypes } from "@/components/PlugIcons";
import { printPacking } from "@/utils/printChecklist";
import { fxData } from "@/data/fx";
import { countries, getCountry } from "@/data/countries";

const TAB_KEY = "hm-country-tab";

const tabs = [
  { id: "visa", label: "Visas", icon: BookOpen },
  { id: "weather", label: "Weather", icon: Sun },
  { id: "cities", label: "Cities", icon: Building2 },
  { id: "languages", label: "Languages", icon: Languages },
  { id: "transport", label: "Transport", icon: Bus },
  { id: "food", label: "Food", icon: UtensilsCrossed },
  { id: "cost", label: "Cost of Living", icon: Wallet },
  { id: "taxes", label: "Taxes", icon: Landmark },
  { id: "healthcare", label: "Healthcare vs US", icon: HeartPulse },
  { id: "packing", label: "Packing List", icon: Luggage },
  { id: "tech", label: "Technology", icon: Plug },
  { id: "rentals", label: "Rentals", icon: Home },
  { id: "reviews", label: "Mom/Sister Reviews", icon: MessageSquareHeart },
];

const TabPanel = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, y: 14 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -8 }}
    transition={{ duration: 0.3 }}
    className="prose-none"
  >
    {children}
  </motion.div>
);

const Section = ({ title, children }) => (
  <div className="mb-8 last:mb-0">
    <h3 className="font-serif font-semibold text-xl text-pine mb-3">{title}</h3>
    {children}
  </div>
);

export default function CountryDetail() {
  const { slug } = useParams();
  const country = getCountry(slug);
  const [tab, setTabState] = useState(() => {
    const saved = sessionStorage.getItem(TAB_KEY);
    return tabs.some((t) => t.id === saved) ? saved : "visa";
  });
  const setTab = (id) => {
    sessionStorage.setItem(TAB_KEY, id);
    setTabState(id);
  };
  if (!country) return <Navigate to="/countries" replace />;
  const related = countries.filter((c) => c.slug !== slug).slice(0, 3);
  const idx = countries.findIndex((c) => c.slug === slug);
  const prev = countries[(idx - 1 + countries.length) % countries.length];
  const next = countries[(idx + 1) % countries.length];

  const tabContent = {
    tech: (() => {
      const t = techData[country.slug];
      if (!t) return null;
      const types = parsePlugTypes(t.plugs);
      const sim = simData[country.slug];
      const cells = [
        ["Plug type", t.plugs],
        ["Voltage & frequency", t.voltage],
        ["Your US devices", t.usDevices],
        ["Power reliability", t.power],
        ["Internet", t.internet],
      ];
      return (
        <div data-testid="tech-tab">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {cells.map(([label, value], i) => (
              <div key={label} className={`rounded-xl p-5 border ${i === 0 ? "bg-pine text-cream border-pine" : "bg-blush border-petal/20"}`} data-testid={`tech-cell-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <p className={`font-mono text-[10px] tracking-[0.25em] uppercase mb-2 ${i === 0 ? "text-petal-soft" : "text-petal"}`}>{label}</p>
                <p className={`text-sm leading-relaxed ${i === 0 ? "font-serif font-semibold text-xl" : "text-ink/85"}`}>{value}</p>
                {i === 0 && (
                  <div className="mt-4 flex flex-wrap gap-4">
                    {types.map((ty) => <PlugIcon key={ty} type={ty} />)}
                  </div>
                )}
              </div>
            ))}
          </div>
          {sim && (
            <div className="bg-white rounded-xl border border-pine/10 p-5 lg:p-6 mb-6" data-testid="sim-guide">
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-petal mb-4">SIM & home internet guide</p>
              <div className="bg-blush rounded-xl border border-petal/20 p-4 mb-5 flex flex-col sm:flex-row sm:items-start gap-3" data-testid="sim-availability">
                <span className="font-mono text-[10px] tracking-[0.2em] uppercase bg-pine text-cream rounded-full px-3 py-1.5 whitespace-nowrap self-start">SIM: {sim.availability}</span>
                <p className="text-sm text-ink/85 leading-relaxed"><span className="font-semibold text-pine">Availability & requirements: </span>{sim.availabilityNote}</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div>
                  <p className="font-serif font-semibold text-pine mb-2">Best carriers</p>
                  <ul className="space-y-1.5">
                    {sim.carriers.map((c) => <li key={c} className="text-sm text-ink/85 flex gap-2"><span className="text-petal shrink-0">✦</span>{c}</li>)}
                  </ul>
                </div>
                <div>
                  <p className="font-serif font-semibold text-pine mb-2">SIM / eSIM on arrival</p>
                  <p className="text-sm text-ink/85 leading-relaxed">{sim.esim}</p>
                </div>
                <div>
                  <p className="font-serif font-semibold text-pine mb-2">Home internet</p>
                  <p className="text-sm text-ink/85 leading-relaxed">{sim.home}</p>
                </div>
              </div>
            </div>
          )}
          <p className="text-sm text-sage italic border-l-2 border-petal pl-4 max-w-2xl" data-testid="tech-tip">
            <span className="font-semibold not-italic text-pine">Mom's adapter tip: </span>{t.tip}
          </p>
        </div>
      );
    })(),
    packing: (() => {
      const p = packingData[country.slug];
      if (!p) return null;
      const tech = techData[country.slug];
      const adapterLine = tech ? `US → ${parsePlugTypes(tech.plugs).join("/")} plug adapters (pack 4+)` : "Universal plug adapters (pack 4+)";
      const groups = [
        ["For mom", p.mom],
        ["For the kids", p.kids],
        ["Health & home", p.health],
        ["Tech & adapters", [adapterLine, "Surge-protector power strip", "Power banks for the whole family", "Unlocked phone for the local SIM"]],
        ["Documents folder", p.docs],
      ];
      return (
        <div data-testid="packing-tab">
          <p className="font-serif italic text-lg text-petal-dark mb-6 max-w-3xl">{p.climateNote}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {groups.map(([title, items]) => (
              <div key={title} className="bg-blush rounded-xl p-5 border border-petal/20">
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-pine mb-3">{title}</p>
                <ul className="space-y-2">
                  {items.map((it) => (
                    <li key={it} className="text-sm text-ink/85 leading-relaxed flex gap-2"><span className="text-petal shrink-0">✦</span>{it}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <button
            data-testid="packing-print-btn"
            onClick={() => printPacking(country, groups)}
            className="inline-flex items-center gap-2 bg-pine text-cream text-sm font-semibold px-6 py-3 rounded-full hover:bg-petal transition-colors"
          >
            Print this packing list
          </button>
        </div>
      );
    })(),
    healthcare: (() => {
      const h = healthcareData[country.slug];
      if (!h) return null;
      return (
        <div data-testid="healthcare-tab">
          <div className="mb-6 max-w-2xl">
            <div className="flex justify-between items-baseline mb-2">
              <span className="font-mono text-xs tracking-[0.25em] uppercase text-pine">Compared to US standards</span>
              <span className="font-mono text-lg text-petal" data-testid="healthcare-us-percent">{h.usPercent}%</span>
            </div>
            <div className="h-2.5 rounded-full bg-blush overflow-hidden">
              <motion.div
                className="h-full rounded-full bg-petal"
                initial={{ width: 0 }}
                animate={{ width: `${h.usPercent}%` }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              />
            </div>
            <p className="mt-2 text-sm font-serif italic text-petal-dark">{h.label}</p>
          </div>
          <p className="text-base text-ink/85 leading-relaxed max-w-3xl mb-6">{h.summary}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-blush rounded-xl p-5 border border-petal/20">
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-pine mb-3">Strong vs the US</p>
              <ul className="space-y-2">
                {h.strengths.map((s) => <li key={s} className="text-sm text-ink/85 leading-relaxed flex gap-2"><span className="text-petal">✦</span>{s}</li>)}
              </ul>
            </div>
            <div className="bg-blush rounded-xl p-5 border border-petal/20">
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-petal mb-3">Weaker vs the US</p>
              <ul className="space-y-2">
                {h.watchOuts.map((s) => <li key={s} className="text-sm text-ink/85 leading-relaxed flex gap-2"><span className="text-petal-dark">✦</span>{s}</li>)}
              </ul>
            </div>
          </div>
          <p className="text-sm text-sage italic border-l-2 border-petal pl-4 max-w-2xl">{h.insuranceNote}</p>
        </div>
      );
    })(),
    visa: (
      <div className="space-y-6">
        <p className="text-lg font-serif italic text-petal-dark">{country.visa.summary}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            ["Tourist entry", country.visa.tourist],
            ["Long stay / nomad / residency", country.visa.longStay],
            ["Student pathways", country.visa.student],
            ["Work pathways", country.visa.work],
          ].map(([t, d]) => (
            <div key={t} className="bg-blush rounded-xl p-5 border border-petal/20">
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-petal mb-2">{t}</p>
              <p className="text-sm text-ink/85 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
        {insuranceReqs[country.slug] && (
          <div className="bg-pine text-cream rounded-xl p-5 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="insurance-requirement">
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase bg-petal text-white rounded-full px-3 py-1.5 whitespace-nowrap self-start">
              {insuranceReqs[country.slug].level}
            </span>
            <p className="text-sm text-cream/85 leading-relaxed">
              <span className="font-semibold text-petal-soft">Health insurance: </span>
              {insuranceReqs[country.slug].detail}
            </p>
          </div>
        )}
        <p className="text-xs text-sage-muted">Always confirm current rules with the destination's embassy or consulate — visa policies change.</p>
      </div>
    ),
    weather: (
      <div>
        <p className="text-lg font-serif italic text-petal-dark mb-6">{country.weather.summary}</p>
        <div className="space-y-3">
          {country.weather.seasons.map((s) => (
            <div key={s.period} className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-6 bg-blush rounded-xl px-5 py-4 border border-petal/20">
              <span className="font-mono text-xs tracking-widest uppercase text-pine w-40 shrink-0">{s.period}</span>
              <span className="text-sm text-ink/85">{s.detail}</span>
            </div>
          ))}
        </div>
      </div>
    ),
    cities: (
      <div className="space-y-3">
        {country.cities.map((c) => (
          <div key={c.name} className="bg-blush rounded-xl p-5 border border-petal/20">
            <p className="font-serif font-semibold text-lg text-pine">{c.name}</p>
            <p className="text-sm text-ink/85 mt-1 leading-relaxed">{c.note}</p>
          </div>
        ))}
      </div>
    ),
    languages: <p className="text-base text-ink/85 leading-relaxed max-w-3xl">{country.languages}</p>,
    transport: <p className="text-base text-ink/85 leading-relaxed max-w-3xl">{country.transport}</p>,
    food: <p className="text-base text-ink/85 leading-relaxed max-w-3xl">{country.food}</p>,
    cost: (
      <div className="max-w-3xl">
        <BudgetEstimator slug={country.slug} />
        <div className="max-w-2xl">
        <div className="bg-white rounded-2xl border border-pine/10 overflow-hidden">
          {Object.entries(country.costOfLiving).map(([k, v], i) => (
            <div key={k} className={`flex items-baseline justify-between gap-6 px-6 py-4 ${i % 2 ? "bg-blush/60" : ""} ${k === "total" ? "bg-pine text-cream" : ""}`}>
              <span className={`text-sm capitalize ${k === "total" ? "font-semibold" : "text-sage"}`}>{k === "rent" ? "Rent (family apartment)" : k}</span>
              <span className={`font-mono text-sm text-right ${k === "total" ? "text-petal-soft" : "text-pine"}`}>{v}</span>
            </div>
          ))}
        </div>
        <p className="mt-3 text-xs text-sage-muted">Estimates in USD for a family of four living modestly-comfortably, 2026. Your barakah may vary.</p>
        </div>
      </div>
    ),
    rentals: (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.entries(country.rentals).map(([k, v]) => (
          <div key={k} className="bg-blush rounded-xl p-5 border border-petal/20">
            <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-petal mb-2">
              {k === "nomad" ? "Digital nomad visa" : k === "residency" ? "Residency permit" : k === "student" ? "Student visa" : "Tourist entry"}
            </p>
            <p className="text-sm text-ink/85 leading-relaxed">{v}</p>
          </div>
        ))}
      </div>
    ),
    taxes: <TaxesTab country={country} />,
    reviews: <CommunityReviews country={country} />,
  };

  return (
    <div data-testid={`country-page-${country.slug}`} className="bg-cream grain relative">
      <Seo
        title={`${country.name} — Hijrah Profile & Mom's Honest Take | Hijrah Mom`}
        siteName="Hijrah Mom"
        description={`Moving to ${country.name} as an American Muslim family: US-citizen visas, weather, cities, languages, transport, halal food, cost of living, rentals, and an honest first-person review.`}
        image={country.image}
      />

      {/* HERO */}
      <section className="relative pt-24 lg:pt-28">
        <div className="relative h-[46vh] lg:h-[58vh] overflow-hidden">
          <img src={country.image} alt={country.imageAlt} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-pine-ink via-pine-ink/40 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pb-10">
            <FadeUp>
              <p className="font-mono text-[11px] tracking-[0.3em] uppercase font-semibold text-cream">{country.region} · Capital: {country.capital}</p>
              <h1 className="mt-3 font-serif font-bold text-cream text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                {country.name} <span className="italic font-medium text-2xl lg:text-3xl text-petal-soft">{country.arabic}</span>
              </h1>
              <p className="mt-3 font-serif italic text-xl lg:text-2xl text-sage-light">"{country.tagline}"</p>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* QUICK FACTS */}
      <section className="bg-pine-ink text-cream border-t border-cream/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
          {Object.entries(country.quickFacts).map(([k, v]) => (
            <div key={k} data-testid={`fact-${k}`}>
              <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-petal-soft">{k.replace(/([A-Z])/g, " $1")}</p>
              <p className="mt-1.5 text-sm text-cream/90 leading-snug">{v}</p>
              {k === "currency" && fxData[country.slug] && (
                <p data-testid="fact-usd-rate" className="mt-1 font-mono text-[11px] text-petal-soft leading-snug">
                  1 USD ≈ {fxData[country.slug].rate}
                </p>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* TABS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-14 lg:py-20">
        <div className="flex items-center justify-between gap-4 mb-8">
          <Link to="/countries" data-testid="country-back-link" className="inline-flex items-center gap-2 text-sm font-semibold text-pine hover:text-petal transition-colors">
            <ArrowLeft size={16} /> All countries
          </Link>
          <div className="flex items-center gap-2" data-testid="country-pager">
            <Link
              to={`/countries/${prev.slug}`}
              data-testid="country-prev-link"
              aria-label={`Previous: ${prev.name}`}
              className="group inline-flex items-center gap-2 border border-pine/20 bg-white rounded-full pl-3 pr-4 py-2 text-sm font-semibold text-pine hover:bg-pine hover:text-cream transition-colors"
            >
              <ChevronLeft size={16} className="group-hover:-translate-x-0.5 transition-transform" />
              <span className="hidden sm:inline">{prev.name}</span>
            </Link>
            <span className="font-mono text-xs text-sage">{idx + 1} / {countries.length}</span>
            <Link
              to={`/countries/${next.slug}`}
              data-testid="country-next-link"
              aria-label={`Next: ${next.name}`}
              className="group inline-flex items-center gap-2 border border-pine/20 bg-white rounded-full pl-4 pr-3 py-2 text-sm font-semibold text-pine hover:bg-pine hover:text-cream transition-colors"
            >
              <span className="hidden sm:inline">{next.name}</span>
              <ChevronRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </div>
        </div>
        <div className="flex flex-wrap gap-2" role="tablist" data-testid="country-tabs">
          {tabs.map((t) => (
            <button
              key={t.id}
              role="tab"
              aria-selected={tab === t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 whitespace-nowrap px-4 sm:px-5 py-2.5 rounded-full text-sm font-semibold transition-colors duration-300 ${
                tab === t.id ? "bg-pine text-cream shadow-md" : "bg-white border border-pine/15 text-pine hover:bg-blush-deep"
              }`}
            >
              <t.icon size={15} className={tab === t.id ? "text-petal-soft" : "text-petal"} />
              {t.label}
            </button>
          ))}
        </div>
        <div className="mt-8 bg-white rounded-3xl border border-pine/10 p-6 lg:p-10 shadow-sm min-h-[280px]">
          <AnimatePresence mode="wait">
            <TabPanel key={tab}>{tabContent[tab]}</TabPanel>
          </AnimatePresence>
        </div>
      </section>

      {country.slug === "tanzania" && <TanzaniaSplit />}

      {/* MOM'S TAKE */}
      <MomTakeSection country={country} />

      {/* RELATED */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-16 lg:py-24">
        <h2 className="font-serif font-semibold text-ink text-2xl lg:text-3xl mb-10">Also on your shortlist</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {related.map((c, i) => (
            <CountryCard key={c.slug} country={c} index={i} />
          ))}
        </div>
      </section>
    </div>
  );
}
